import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, TextField, Button, Paper,
    IconButton, Alert, CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ArrowBack as BackIcon,
    Bolt as FlashIcon,
    AccessTime as TimeIcon,
    Notes as NotesIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { addMinutes } from 'date-fns';
import api from '../services/api';

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.08) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const FadeUp = ({ children, delay = 0 }) => {
    const [ref, inView] = useInView();
    return (
        <Box ref={ref} sx={{
            opacity:   inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(28px)',
            transition: `opacity 0.5s ease ${delay}s, transform 0.5s ease ${delay}s`,
        }}>
            {children}
        </Box>
    );
};

// ── Constants ─────────────────────────────────────────────────────────────────
const DURATIONS = [
    { label: '15 min', minutes: 15 },
    { label: '30 min', minutes: 30 },
    { label: '45 min', minutes: 45 },
    { label: '1 hr',   minutes: 60 },
    { label: '90 min', minutes: 90 },
    { label: '2 hr',   minutes: 120 },
];

const toLocalISO = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const SectionCard = ({ icon, label, children, delay = 0 }) => (
    <FadeUp delay={delay}>
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden', borderColor: 'rgba(0,0,0,0.1)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {React.cloneElement(icon, { sx: { fontSize: 16, color: '#7C3AED' } })}
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#374151' }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ px: 3, py: 3, bgcolor: 'white' }}>
                {children}
            </Box>
        </Paper>
    </FadeUp>
);

// ── Component ─────────────────────────────────────────────────────────────────
const CreateQuickMeet = () => {
    const navigate  = useNavigate();
    const scrollY   = useScrollY();

    const [title,        setTitle]       = useState('');
    const [note,         setNote]        = useState('');
    const [duration,     setDuration]    = useState(30);
    const [slots,        setSlots]       = useState([null, null]);
    const [error,        setError]       = useState('');
    const [submitting,   setSubmitting]  = useState(false);

    const updateSlot = (i, val) => setSlots(prev => prev.map((s, idx) => idx === i ? val : s));
    const addSlot    = () => setSlots(prev => [...prev, null]);
    const removeSlot = (i) => setSlots(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!title.trim()) { setError('A title is required.'); return; }
        const validSlots = slots.filter(Boolean);
        if (validSlots.length < 2) { setError('Add at least two time options.'); return; }

        setSubmitting(true);
        try {
            const payload = {
                title: title.trim(),
                note:  note.trim(),
                durationMinutes: duration,
                slots: validSlots.map(start => ({
                    proposedStartTime: toLocalISO(start),
                    proposedEndTime:   toLocalISO(addMinutes(new Date(start), duration)),
                })),
            };
            const res = await api.post('/quick-meets', payload);
            navigate(`/quick-meets/${res.data.id}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

            {/* Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', py: 6, position: 'relative', overflow: 'clip' }}>
                <Box sx={{ position:'absolute', top:-50, left:'-3%', width:340, height:340, borderRadius:'50%', background:'rgba(255,255,255,0.07)', transform:`translateY(${scrollY*0.3}px)`, pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', top:-10, right:'4%',  width:240, height:240, borderRadius:'50%', background:'rgba(255,255,255,0.05)', transform:`translateY(${scrollY*0.5}px)`, pointerEvents:'none' }} />

                <Container maxWidth="lg" sx={{ position:'relative', zIndex:1, transform:`translateY(${scrollY*0.2}px)` }}>
                    <Button onClick={() => navigate('/dashboard')} startIcon={<BackIcon />}
                        sx={{ color:'rgba(255,255,255,0.7)', fontWeight:600, mb:2, px:0, '&:hover':{ color:'white', bgcolor:'transparent' } }}>
                        Dashboard
                    </Button>
                    <Typography variant="h4" fontWeight={800} sx={{ color:'white', mb:0.5 }}>Quick Meet</Typography>
                    <Typography variant="body1" sx={{ color:'rgba(255,255,255,0.7)' }}>
                        Propose 2–3 times. They pick one. Done.
                    </Typography>
                </Container>
            </Box>

            {/* Form */}
            <Container maxWidth="lg" sx={{ pt: 5, pb: 10 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:3 }}>

                    {/* Details */}
                    <SectionCard icon={<CalendarIcon />} label="Meeting Details" delay={0.05}>
                        <Box sx={{ display:'flex', flexDirection:'column', gap:3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75 }}>
                                    What's this meeting for? *
                                </Typography>
                                <TextField fullWidth variant="outlined" required size="small"
                                    placeholder="e.g. Coffee chat, Quick sync, Interview"
                                    value={title} onChange={e => setTitle(e.target.value)}
                                    inputProps={{ style: { fontSize: 17, fontWeight: 700 } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75 }}>
                                    Note for the recipient
                                </Typography>
                                <TextField fullWidth variant="outlined" multiline rows={2} size="small"
                                    placeholder="Anything they should know before confirming (optional)"
                                    value={note} onChange={e => setNote(e.target.value)}
                                    InputProps={{ startAdornment: <NotesIcon sx={{ fontSize:18, color:'text.disabled', mr:1, mt:0.25 }} /> }}
                                />
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* Duration */}
                    <SectionCard icon={<TimeIcon />} label="Duration" delay={0.1}>
                        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
                            {DURATIONS.map(d => (
                                <Box key={d.minutes} onClick={() => setDuration(d.minutes)} sx={{
                                    px:2.5, py:0.9, borderRadius:99, cursor:'pointer',
                                    border:'1.5px solid',
                                    borderColor: duration===d.minutes ? '#7C3AED' : '#E5E7EB',
                                    bgcolor:     duration===d.minutes ? '#7C3AED' : 'white',
                                    transition:'all 0.15s',
                                    '&:hover':{ borderColor:'#7C3AED', bgcolor: duration===d.minutes ? '#6D28D9' : '#EDE9FE' },
                                }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: duration===d.minutes ? 'white' : 'text.primary' }}>
                                        {d.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* Time options */}
                    <SectionCard icon={<AddIcon />} label="Propose Times" delay={0.15}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                            Add 2 or 3 specific times that work for you. The recipient picks one and it's instantly confirmed.
                        </Typography>
                        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                            {slots.map((slot, i) => (
                                <Paper key={i} elevation={0} variant="outlined" sx={{ borderRadius:3, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>
                                    <Box sx={{ px:2.5, py:1.25, bgcolor:'#FAFAFA', borderBottom:'1px solid', borderColor:'divider', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                        <Typography variant="caption" fontWeight={700} sx={{ color:'#7C3AED', textTransform:'uppercase', letterSpacing:0.8 }}>
                                            Option {i + 1}
                                        </Typography>
                                        {slots.length > 2 && (
                                            <IconButton size="small" onClick={() => removeSlot(i)}
                                                sx={{ p:0.5, '&:hover':{ bgcolor:'#FEE2E2', color:'#DC2626' } }}>
                                                <DeleteIcon sx={{ fontSize:16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box sx={{ px:2.5, py:2.5 }}>
                                        <DateTimePicker
                                            label="Date & time"
                                            value={slot}
                                            onChange={val => updateSlot(i, val)}
                                            disablePast
                                            slotProps={{ textField: { size:'small', fullWidth:true } }}
                                        />
                                        {slot && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt:0.75, display:'block' }}>
                                                Ends at {new Date(addMinutes(new Date(slot), duration)).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                                            </Typography>
                                        )}
                                    </Box>
                                </Paper>
                            ))}

                            {slots.length < 4 && (
                                <Button startIcon={<AddIcon />} onClick={addSlot} size="small"
                                    sx={{ alignSelf:'flex-start', fontWeight:700, color:'#7C3AED', borderRadius:99, px:2, py:0.8, border:'1.5px dashed #A78BFA', '&:hover':{ bgcolor:'#EDE9FE', borderColor:'#7C3AED' } }}>
                                    Add another option
                                </Button>
                            )}
                        </Box>
                    </SectionCard>

                    {error && (
                        <FadeUp>
                            <Alert severity="error" sx={{ borderRadius:3 }}>{error}</Alert>
                        </FadeUp>
                    )}

                    <FadeUp delay={0.2}>
                        <Box sx={{ display:'flex', gap:2 }}>
                            <Button type="submit" variant="contained" size="large" disabled={submitting}
                                sx={{ fontWeight:700, borderRadius:2.5, px:5, py:1.5, fontSize:'1rem', background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)', '&:hover':{ boxShadow:'0 6px 20px rgba(124,58,237,0.45)' } }}>
                                {submitting ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Create & Get Link'}
                            </Button>
                            <Button variant="outlined" size="large" onClick={() => navigate('/dashboard')}
                                sx={{ fontWeight:700, borderRadius:2.5, borderColor:'rgba(0,0,0,0.2)', color:'text.secondary', '&:hover':{ borderColor:'#7C3AED', color:'#7C3AED' } }}>
                                Cancel
                            </Button>
                        </Box>
                    </FadeUp>

                </Box>
            </Container>
        </Box>
    );
};

export default CreateQuickMeet;
